/* xPT Notification */
export interface XptNotification {
  notification_id: string;
  source: string; // producer id, e.g. "access_management"
  event: string; // producer event/verb, e.g. "submit" | "approve" | ...
  payload: string; // AWSJSON (JSON-serialized string)
  is_persistent: boolean;
  is_read: boolean;
}

/** The codegen'd subscription payload: every non-key field is nullable, and the publish path never
 * sends is_read (PublishNotificationInput has no such field), so it always arrives absent. */
interface PushedNotification {
  notification_id: string;
  source: string;
  event: string;
  payload?: string | null;
  is_persistent?: boolean | null;
  is_read?: boolean | null;
}

/** Normalize a pushed (subscription) notification into the domain shape, coalescing the nullable
 * fields: no payload -> "{}", missing flags -> false. A pushed item is unread (is_read=false), which
 * is correct — it was just published. */
export const fromPushedNotification = (n: PushedNotification): XptNotification => ({
  notification_id: n.notification_id,
  source: n.source,
  event: n.event,
  payload: n.payload ?? '{}',
  is_persistent: n.is_persistent ?? false,
  is_read: n.is_read ?? false
});

/** Whether a notification counts as unread. A persistent item ("action still pending until
 * resolved") is always unread regardless of the read watermark; otherwise it's unread until read.
 * Single source of truth for the bell badge count and the panel's unread dot. */
export const isUnread = (n: XptNotification): boolean => !n.is_read || n.is_persistent;

/* Access-management notification payload */

export const ACCESS_MANAGEMENT_SOURCE = 'access_management';

export type AccessRequestKind = 'SUBMITTED' | 'APPROVED' | 'REJECTED' | 'CANCELLED';

interface AccessRequestBase {
  request_id: string;
  bg_id: number;
  business_group_name: string;
  requested_role: string;
  cost_centers: string[];
  requester_alias: string;
  requester_name: string;
  beneficiary_alias: string;
  beneficiary_name: string;
}

export type ResolutionAction = 'APPROVED' | 'REJECTED' | 'CANCELLED';

interface ApprovalOutcome {
  is_partial_approval?: boolean;
  requested_cost_centers?: string[];
  granted_cost_centers?: string[];
}

export interface SubmittedAccessRequest extends AccessRequestBase, ApprovalOutcome {
  kind: 'SUBMITTED';
  resolution_action?: ResolutionAction;
  resolved_by?: string;
  resolved_by_name?: string;
}

/** The requester/beneficiary's standalone outcome item (own type: APPROVED / REJECTED). */
export interface ResolvedAccessRequest extends AccessRequestBase, ApprovalOutcome {
  kind: 'APPROVED' | 'REJECTED' | 'CANCELLED';
  resolved_by?: string;
  resolved_by_name?: string;
}

export type AccessRequestNotification = SubmittedAccessRequest | ResolvedAccessRequest;

const KIND_BY_EVENT: Record<string, AccessRequestKind> = {
  submit: 'SUBMITTED',
  approve: 'APPROVED',
  reject: 'REJECTED',
  cancel: 'CANCELLED'
};

export const parseAccessRequestNotification = (notification: XptNotification): AccessRequestNotification | null => {
  if (notification.source !== ACCESS_MANAGEMENT_SOURCE) return null;
  const kind = KIND_BY_EVENT[notification.event];
  if (!kind) return null;
  try {
    const payload = JSON.parse(notification.payload ?? '');
    // Guard against a malformed payload (e.g. a double-encoded string that parses to a string, or a
    // non-object). Returning null here makes the presenter fall back to the generic message instead
    // of rendering "undefined (undefined) requested undefined for undefined".
    if (typeof payload !== 'object' || payload === null || !payload.request_id) {
      console.error('parseAccessRequestNotification: unexpected payload shape', {
        notification_id: notification.notification_id,
        event: notification.event,
        payload: notification.payload
      });
      return null;
    }
    return { ...payload, kind } as AccessRequestNotification;
  } catch (e) {
    console.error('parseAccessRequestNotification: payload is not valid JSON', {
      notification_id: notification.notification_id,
      payload: notification.payload,
      error: e
    });
    return null;
  }
};
